import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { dismissToast, useToasts, type ToastVariant } from '@/hooks/useToast';
import { IconButton } from './IconButton';

const VARIANT: Record<ToastVariant, { box: string; Icon: typeof Info }> = {
  success: { box: 'border-success-200 text-success-800 dark:border-success-700', Icon: CheckCircle2 },
  error: { box: 'border-danger-200 text-danger-800 dark:border-danger-700', Icon: AlertCircle },
  warning: { box: 'border-warning-200 text-warning-800 dark:border-warning-700', Icon: TriangleAlert },
  info: { box: 'border-brand-200 text-brand-800 dark:border-brand-700', Icon: Info },
};

/**
 * Mount exactly once, in the app shell. Components fire toasts through
 * `useToast()`; nothing else touches this.
 *
 * Bottom-right on desktop, full-width at the bottom on phones where a
 * fixed-width corner stack would overlap content.
 */
export function ToastHost({ dismissLabel = 'Dismiss' }: { dismissLabel?: string }) {
  const toasts = useToasts();
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      // polite, not assertive: the queue as a whole shouldn't interrupt. The
      // error variant escalates individually below.
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-80"
    >
      {toasts.map((t) => {
        const { box, Icon } = VARIANT[t.variant];
        return (
          <div
            key={t.id}
            role={t.variant === 'error' ? 'alert' : 'status'}
            className={cn('card fade-in pointer-events-auto flex items-start gap-2 border p-3 shadow-lg', box)}
          >
            <Icon size={16} className="mt-0.5 shrink-0" aria-hidden />
            <p className="min-w-0 flex-1 text-sm text-neutral-800">{t.message}</p>
            <IconButton aria-label={dismissLabel} size="sm" onClick={() => dismissToast(t.id)}>
              <X size={14} />
            </IconButton>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
